import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Filter, TrendingUp, DollarSign, Users, MapPin, Utensils, X, RefreshCw, ChevronDown, Search } from 'lucide-react';
import API_BASE_URL from '../../../config/baseApi';

const API_TIMEOUT = 30000;

export default function ReportsModal({ isOpen, onClose, initialReportType = 'reservation' }) {
  const [activeReport, setActiveReport] = useState(initialReportType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  
  // Filters for Reservation Report
  const [reservationFilters, setReservationFilters] = useState({
    from_date: '',
    to_date: '',
    guest: '',
    meal_plan: '',
    guest_from_cat: '',
    country: ''
  });

  // Filters for Registration Report
  const [registrationFilters, setRegistrationFilters] = useState({
    re_guest: '',
    re_meal_plan: '',
    re_guest_from_cat: '',
    re_country: ''
  });

  // Filters for Registration History Report
  const [regHistoryFilters, setRegHistoryFilters] = useState({
    reg_from_date: '',
    reg_to_date: '',
    reg_guest: '',
    reg_meal_plan: '',
    reg_guest_from_cat: '',
    reg_country: ''
  });

  // Filters for Summary Report
  const [summaryFilters, setSummaryFilters] = useState({
    report_type: 'meal_plan',
    from_date: '',
    to_date: ''
  });

  // Filters for Day End Report
  const [dayEndFilters, setDayEndFilters] = useState({
    date: new Date().toISOString().split('T')[0]
  });

  // Filters for Transaction Report
  const [transactionFilters, setTransactionFilters] = useState({
    trans_from_date: '',
    trans_to_date: '',
    expenses: ''
  });

  // Update active report when initialReportType changes
  useEffect(() => {
    if (isOpen) {
      setActiveReport(initialReportType);
      setReportData(null);
      setError(null);
    }
  }, [isOpen, initialReportType]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Fetch report data
  const fetchReport = async (reportType) => {
    try {
      setLoading(true);
      setError(null);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      let url = '';
      let params = new URLSearchParams();

      switch (reportType) {
        case 'reservation':
          url = `${API_BASE_URL}/api/dashboard/reservation-report`;
          Object.entries(reservationFilters).forEach(([key, value]) => {
            if (value) params.append(key, value);
          });
          break;

        case 'registration':
          url = `${API_BASE_URL}/api/dashboard/registration-report`;
          Object.entries(registrationFilters).forEach(([key, value]) => {
            if (value) params.append(key, value);
          });
          break;

        case 'registration-history':
          url = `${API_BASE_URL}/api/dashboard/registration-history-report`;
          Object.entries(regHistoryFilters).forEach(([key, value]) => {
            if (value) params.append(key, value);
          });
          break;

        case 'summary':
          url = `${API_BASE_URL}/api/dashboard/summary-report`;
          Object.entries(summaryFilters).forEach(([key, value]) => {
            if (value) params.append(key, value);
          });
          break;

        case 'day-end':
          url = `${API_BASE_URL}/api/dashboard/day-end-report`;
          if (dayEndFilters.date) params.append('date', dayEndFilters.date);
          break;

        case 'transaction':
          url = `${API_BASE_URL}/api/dashboard/transaction-report`;
          Object.entries(transactionFilters).forEach(([key, value]) => {
            if (value) params.append(key, value);
          });
          break;

        default:
          throw new Error('Invalid report type');
      }

      const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setReportData(result);
        setError(null);
      } else {
        throw new Error(result.message || 'Failed to load report');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timeout - please try again');
      } else {
        setError(err.message);
      }
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle report generation
  const handleGenerateReport = () => {
    fetchReport(activeReport);
  };

  // Reset filters
  const handleResetFilters = () => {
    switch (activeReport) {
      case 'reservation':
        setReservationFilters({
          from_date: '',
          to_date: '',
          guest: '',
          meal_plan: '',
          guest_from_cat: '',
          country: ''
        });
        break;
      case 'registration':
        setRegistrationFilters({
          re_guest: '',
          re_meal_plan: '',
          re_guest_from_cat: '',
          re_country: ''
        });
        break;
      case 'registration-history':
        setRegHistoryFilters({
          reg_from_date: '',
          reg_to_date: '',
          reg_guest: '',
          reg_meal_plan: '',
          reg_guest_from_cat: '',
          reg_country: ''
        });
        break;
      case 'summary':
        setSummaryFilters({
          report_type: 'meal_plan',
          from_date: '',
          to_date: ''
        });
        break;
      case 'day-end':
        setDayEndFilters({
          date: new Date().toISOString().split('T')[0]
        });
        break;
      case 'transaction':
        setTransactionFilters({
          trans_from_date: '',
          trans_to_date: '',
          expenses: ''
        });
        break;
    }
    setReportData(null);
  };

  // Export to CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const reportTypes = [
    { id: 'reservation', name: 'Reservation Report', icon: Calendar, color: 'purple' },
    { id: 'registration', name: 'Registration Report', icon: Users, color: 'blue' },
    { id: 'registration-history', name: 'Registration History', icon: FileText, color: 'green' },
    { id: 'summary', name: 'Summary Report', icon: TrendingUp, color: 'orange' },
    { id: 'day-end', name: 'Day End Report', icon: DollarSign, color: 'red' },
    { id: 'transaction', name: 'Transaction Report', icon: DollarSign, color: 'indigo' }
  ];

  const colorClasses = {
    purple: { bg: 'from-purple-500 to-purple-600', light: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-600 dark:text-purple-400' },
    blue: { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-600 dark:text-blue-400' },
    green: { bg: 'from-green-500 to-green-600', light: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-600 dark:text-green-400' },
    orange: { bg: 'from-orange-500 to-orange-600', light: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-600 dark:text-orange-400' },
    red: { bg: 'from-red-500 to-red-600', light: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-600 dark:text-red-400' },
    indigo: { bg: 'from-indigo-500 to-indigo-600', light: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-800', text: 'text-indigo-600 dark:text-indigo-400' }
  };

  // Render filter sections based on active report
  const renderFilters = () => {
    switch (activeReport) {
      case 'reservation':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">From Date</label>
              <input
                type="date"
                value={reservationFilters.from_date}
                onChange={(e) => setReservationFilters({ ...reservationFilters, from_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">To Date</label>
              <input
                type="date"
                value={reservationFilters.to_date}
                onChange={(e) => setReservationFilters({ ...reservationFilters, to_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guest Name</label>
              <input
                type="text"
                value={reservationFilters.guest}
                onChange={(e) => setReservationFilters({ ...reservationFilters, guest: e.target.value })}
                placeholder="Enter guest name"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Meal Plan</label>
              <select
                value={reservationFilters.meal_plan}
                onChange={(e) => setReservationFilters({ ...reservationFilters, meal_plan: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="BB">Bed & Breakfast</option>
                <option value="HB">Half Board</option>
                <option value="FB">Full Board</option>
                <option value="RO">Room Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guest Category</label>
              <input
                type="text"
                value={reservationFilters.guest_from_cat}
                onChange={(e) => setReservationFilters({ ...reservationFilters, guest_from_cat: e.target.value })}
                placeholder="Enter category"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
              <input
                type="text"
                value={reservationFilters.country}
                onChange={(e) => setReservationFilters({ ...reservationFilters, country: e.target.value })}
                placeholder="Enter country"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'registration':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guest Name</label>
              <input
                type="text"
                value={registrationFilters.re_guest}
                onChange={(e) => setRegistrationFilters({ ...registrationFilters, re_guest: e.target.value })}
                placeholder="Enter guest name"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Meal Plan</label>
              <select
                value={registrationFilters.re_meal_plan}
                onChange={(e) => setRegistrationFilters({ ...registrationFilters, re_meal_plan: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="BB">Bed & Breakfast</option>
                <option value="HB">Half Board</option>
                <option value="FB">Full Board</option>
                <option value="RO">Room Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guest Category</label>
              <input
                type="text"
                value={registrationFilters.re_guest_from_cat}
                onChange={(e) => setRegistrationFilters({ ...registrationFilters, re_guest_from_cat: e.target.value })}
                placeholder="Enter category"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
              <input
                type="text"
                value={registrationFilters.re_country}
                onChange={(e) => setRegistrationFilters({ ...registrationFilters, re_country: e.target.value })}
                placeholder="Enter country"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'registration-history':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">From Date</label>
              <input
                type="date"
                value={regHistoryFilters.reg_from_date}
                onChange={(e) => setRegHistoryFilters({ ...regHistoryFilters, reg_from_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">To Date</label>
              <input
                type="date"
                value={regHistoryFilters.reg_to_date}
                onChange={(e) => setRegHistoryFilters({ ...regHistoryFilters, reg_to_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guest Name</label>
              <input
                type="text"
                value={regHistoryFilters.reg_guest}
                onChange={(e) => setRegHistoryFilters({ ...regHistoryFilters, reg_guest: e.target.value })}
                placeholder="Enter guest name"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Meal Plan</label>
              <select
                value={regHistoryFilters.reg_meal_plan}
                onChange={(e) => setRegHistoryFilters({ ...regHistoryFilters, reg_meal_plan: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="BB">Bed & Breakfast</option>
                <option value="HB">Half Board</option>
                <option value="FB">Full Board</option>
                <option value="RO">Room Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guest Category</label>
              <input
                type="text"
                value={regHistoryFilters.reg_guest_from_cat}
                onChange={(e) => setRegHistoryFilters({ ...regHistoryFilters, reg_guest_from_cat: e.target.value })}
                placeholder="Enter category"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
              <input
                type="text"
                value={regHistoryFilters.reg_country}
                onChange={(e) => setRegHistoryFilters({ ...regHistoryFilters, reg_country: e.target.value })}
                placeholder="Enter country"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Type</label>
              <select
                value={summaryFilters.report_type}
                onChange={(e) => setSummaryFilters({ ...summaryFilters, report_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="meal_plan">By Meal Plan</option>
                <option value="guest_country">By Country</option>
                <option value="guest_from_cat">By Guest Category</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">From Date</label>
              <input
                type="date"
                value={summaryFilters.from_date}
                onChange={(e) => setSummaryFilters({ ...summaryFilters, from_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">To Date</label>
              <input
                type="date"
                value={summaryFilters.to_date}
                onChange={(e) => setSummaryFilters({ ...summaryFilters, to_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'day-end':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
              <input
                type="date"
                value={dayEndFilters.date}
                onChange={(e) => setDayEndFilters({ ...dayEndFilters, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'transaction':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">From Date</label>
              <input
                type="date"
                value={transactionFilters.trans_from_date}
                onChange={(e) => setTransactionFilters({ ...transactionFilters, trans_from_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">To Date</label>
              <input
                type="date"
                value={transactionFilters.trans_to_date}
                onChange={(e) => setTransactionFilters({ ...transactionFilters, trans_to_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expense Category</label>
              <input
                type="text"
                value={transactionFilters.expenses}
                onChange={(e) => setTransactionFilters({ ...transactionFilters, expenses: e.target.value })}
                placeholder="Optional expense category"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render report data (same as before, truncated for brevity - I'll include the full implementation)
  const renderReportData = () => {
    if (!reportData) return null;

    const currentReport = reportTypes.find(r => r.id === activeReport);
    const colors = colorClasses[currentReport.color];

    switch (activeReport) {
      case 'reservation':
        return (
          <div className="overflow-x-auto max-h-96">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Guest Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Meal Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Country</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {reportData.reservations?.map((reservation, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">{reservation.guest_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{reservation.reservation_date}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${colors.light} ${colors.text}`}>
                        {reservation.meal_plan}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{reservation.guest_from_cat || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{reservation.guest_country || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reportData.reservations?.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No reservations found matching the criteria
              </div>
            )}
          </div>
        );

      case 'registration':
        return (
          <div className="overflow-x-auto max-h-96">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Guest Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Room</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Meal Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Country</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {reportData.registrations?.map((registration, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">{registration.guest_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{registration.allocated_room_no || registration.room_no}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${colors.light} ${colors.text}`}>
                        {registration.meal_plan}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{registration.guest_from_cat || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{registration.guest_country || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reportData.registrations?.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No registrations found matching the criteria
              </div>
            )}
          </div>
        );

      case 'registration-history':
        return (
          <div className="overflow-x-auto max-h-96">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Guest Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Room</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Meal Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Country</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {reportData.registration_history?.map((history, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">{history.guest_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{history.allocated_room_no || history.room_no}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{history.reservation_date}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${colors.light} ${colors.text}`}>
                        {history.meal_plan}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{history.guest_from_cat || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{history.guest_country || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reportData.registration_history?.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No history found matching the criteria
              </div>
            )}
          </div>
        );

      case 'summary':
        return (
          <div>
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Report Type</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">
                    {reportData.reportType?.replace('_', ' ')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Count</p>
                  <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{reportData.totalCount}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {reportData.summary?.map((item, idx) => (
                <div key={idx} className={`p-6 ${colors.light} border ${colors.border} rounded-xl`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{item.name || 'N/A'}</p>
                      <p className={`text-2xl font-bold ${colors.text}`}>{item.count}</p>
                    </div>
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.bg}`}>
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">Percentage</span>
                      <span className={`font-bold ${colors.text}`}>
                        {((item.count / reportData.totalCount) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'day-end':
        const totalIncome = reportData.incomes?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;
        const totalExpenses = reportData.expenses?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;
        const netAmount = totalIncome - totalExpenses;

        return (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Income</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  ${totalIncome.toFixed(2)}
                </p>
              </div>
              <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Expenses</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  ${totalExpenses.toFixed(2)}
                </p>
              </div>
              <div className={`p-6 ${netAmount >= 0 ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'} border rounded-xl`}>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Net Amount</p>
                <p className={`text-3xl font-bold ${netAmount >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'}`}>
                  ${netAmount.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3">
                  <h3 className="text-sm font-bold text-white flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Income Transactions
                  </h3>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Bill No</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Category</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {reportData.incomes?.map((income, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{income.bill_no}</td>
                          <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{income.ex_cat || '-'}</td>
                          <td className="px-4 py-2 text-sm text-right text-green-600 dark:text-green-400 font-bold">
                            ${parseFloat(income.amount).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportData.incomes?.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                      No income transactions
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3">
                  <h3 className="text-sm font-bold text-white flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Expense Transactions
                  </h3>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Bill No</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Category</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {reportData.expenses?.map((expense, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{expense.bill_no}</td>
                          <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{expense.ex_cat || '-'}</td>
                          <td className="px-4 py-2 text-sm text-right text-red-600 dark:text-red-400 font-bold">
                            ${parseFloat(expense.amount).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportData.expenses?.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                      No expense transactions
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'transaction':
        const txTotalIncome = reportData.incomes?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;
        const txTotalExpenses = reportData.expenses?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;
        const txNetAmount = txTotalIncome - txTotalExpenses;

        return (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Income</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  ${txTotalIncome.toFixed(2)}
                </p>
              </div>
              <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Expenses</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  ${txTotalExpenses.toFixed(2)}
                </p>
              </div>
              <div className={`p-6 ${txNetAmount >= 0 ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'} border rounded-xl`}>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Net Amount</p>
                <p className={`text-3xl font-bold ${txNetAmount >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'}`}>
                  ${txNetAmount.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Bill No</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Category</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {[...(reportData.incomes || []), ...(reportData.expenses || [])]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((transaction, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{transaction.date}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">{transaction.bill_no}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                            transaction.status === 'Income' 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{transaction.ex_cat || '-'}</td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${
                          transaction.status === 'Income'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          ${parseFloat(transaction.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {[...(reportData.incomes || []), ...(reportData.expenses || [])].length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No transactions found for the selected period
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  const currentReport = reportTypes.find(r => r.id === activeReport);
  const colors = colorClasses[currentReport?.color || 'blue'];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${colors.bg} px-6 py-5 flex items-center justify-between sticky top-0 z-10`}>
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 text-white" />
                <h2 className="text-xl font-bold text-white">Reports</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Report Type Selector */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {reportTypes.map((report) => {
                  const Icon = report.icon;
                  const isActive = activeReport === report.id;
                  const reportColors = colorClasses[report.color];
                  
                  return (
                    <button
                      key={report.id}
                      onClick={() => {
                        setActiveReport(report.id);
                        setReportData(null);
                      }}
                      className={`p-3 rounded-xl border-2 transition-all duration-300 ${
                        isActive
                          ? `${reportColors.light} ${reportColors.border} shadow-lg scale-105`
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md hover:scale-102'
                      }`}
                    >
                      <div className="flex flex-col items-center text-center space-y-2">
                        <div className={`p-2 rounded-xl ${isActive ? `bg-gradient-to-br ${reportColors.bg}` : 'bg-gray-100 dark:bg-gray-700'}`}>
                          <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                        </div>
                        <span className={`text-xs font-semibold ${isActive ? reportColors.text : 'text-gray-700 dark:text-gray-300'}`}>
                          {report.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Filters Section */}
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-6 mb-6">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </h3>
                {renderFilters()}
                <div className="flex items-center justify-end space-x-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={handleResetFilters}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg transition-colors flex items-center space-x-2 text-sm"
                  >
                    <X className="w-4 h-4" />
                    <span>Reset</span>
                  </button>
                  <button
                    onClick={handleGenerateReport}
                    disabled={loading}
                    className={`px-4 py-2 bg-gradient-to-r ${colors.bg} hover:opacity-90 text-white font-semibold rounded-lg transition-all flex items-center space-x-2 disabled:opacity-50 text-sm`}
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        <span>Generate Report</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs">!</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-red-900 dark:text-red-100 mb-1">Error Loading Report</h3>
                      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Report Data Display */}
              {reportData && !error && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className={`bg-gradient-to-r ${colors.bg} px-6 py-4 flex items-center justify-between`}>
                    <h3 className="text-lg font-bold text-white flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      {currentReport?.name}
                    </h3>
                    <button
                      onClick={() => {
                        const dataToExport = reportData.reservations || 
                                            reportData.registrations || 
                                            reportData.registration_history ||
                                            reportData.summary ||
                                            [...(reportData.incomes || []), ...(reportData.expenses || [])];
                        exportToCSV(dataToExport, activeReport);
                      }}
                      className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-colors flex items-center space-x-2 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export CSV</span>
                    </button>
                  </div>
                  <div className="p-6">
                    {renderReportData()}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!reportData && !loading && !error && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12">
                  <div className="text-center">
                    <div className={`inline-flex p-4 rounded-full ${colors.light} mb-4`}>
                      <FileText className={`w-10 h-10 ${colors.text}`} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      No Report Generated Yet
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Select your filters and click "Generate Report" to view data
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}